import { Component, OnInit, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { BUSNUserService, BUSNQuestionService } from '@takectrl-multiapp/svcs-core-business';
import { IAddresses, IGeneralCodeDescriptionType, IQuestion, SharedConfig, SharedDialogService } from '@takectrl-multiapp/svcs-share-config';

@Component({
  selector: 'address-comp',
  standalone: false,
  templateUrl: './address-comp.component.html',
  styleUrls: ['./address-comp.component.scss']
})
export class AddressCompComponent implements OnInit {
  @Output() addressSelected: EventEmitter<number> = new EventEmitter();
  @Input() question: IQuestion;
  @ViewChild('searchInput') searchInputRef: ElementRef<HTMLInputElement>;
  addresses: Array<IAddresses> = new Array<IAddresses>;
  newAddressPopup: boolean = false;
  selectPopup: boolean = false;
  selectPopHeading: string;
  newAddressPayload: IAddresses;
  selectData: Array<IGeneralCodeDescriptionType>;
  fieldName: string;
  provinceCode: string;
  suburbCode: string;
  addressType: string;
  searchFound: Array<any> | undefined;
  is_searchable: boolean = true;
  allow_free_text: boolean = false;
  sub_loader: boolean = false;
  searchResults: Array<IGeneralCodeDescriptionType> = new Array;
  searchCannel: string = 'local';
  hideDefualtResults: boolean = false;
  selectedAddress: IAddresses;
  free_text_message: boolean = false;
  manual_text: string = '';
  constructor(
    private readonly busnUserService: BUSNUserService,
    public sharedConfig: SharedConfig,
    private busnQuestionService: BUSNQuestionService,
    public sharedDialogService: SharedDialogService
  ) { }

  async ngOnInit() {
    this.addresses = (await this.busnUserService.getUserAddresses()).sort((a, b) => b.id - a.id);
    if (this.question.underwriting_answers[0].object_answer) {
      let getUserAddress = await this.busnUserService.getAddressBasedOnID(this.question.underwriting_answers[0].object_answer)
      this.onAddressSelect(getUserAddress)
    }
    this.cleanPayload();
  }

  cleanPayload() {
    this.newAddressPayload = {
      user_id: null,
      active: null,
      address_type: null,
      id: null,
      asset_type: null,
      asset_id: null,
      postal_code: null,
      risk_address_code: null,
      risk_address_code_id: null,
      risk_address_code_id_mnfg: null,
      risk_address_code_id_sa02: null,
      risk_address_line1: null,
      risk_address_line2: null,
      city: null,
      country: null,
      district: null,
      latitude: null,
      longitude: null,
      province: null,
      street_name: null,
      street_number: null,
      suburb: null,
      unit_number: null,
      sectional_title: null,
      estate: null,
    };
  }

  onAddressSelect(selectedAddress: IAddresses) {
    this.addressSelected.emit(selectedAddress.id);
    this.selectedAddress = selectedAddress;
  }

  compareAddress(a1: IAddresses, a2: IAddresses): boolean {
    return a1 && a2 ? a1.id === a2.id : a1 === a2;
  }

  addNewAddress() {
    this.newAddressPopup = true;
    this.selectedAddress = null;
  }

  async showSelect(field_name: string) {
    this.sharedConfig.setShowCustomLoader(true);
    this.fieldName = field_name;
    this.selectData = new Array;
    try {
      switch (field_name) {
        case 'province':
          this.selectData = await this.busnUserService.addressProvinces();
          this.is_searchable = false;
          this.showSelectPopup('Province');
          break;
        case 'suburb':
          this.selectData = await this.busnUserService.addressSuburb(this.provinceCode);
          this.is_searchable = true;
          this.searchCannel = 'local';
          this.showSelectPopup('Suburb');
          break;
        case 'address_type':
          this.selectData = [
            {
              code: 'street',
              description: 'Street'
            },
            {
              code: 'estate',
              description: 'Estate'
            },
            {
              code: 'sectional',
              description: 'Sectional Title'
            }
          ];
          this.is_searchable = false;
          this.showSelectPopup('Address type');
          break;
        case 'street_name':
          try {
            this.selectData = await this.busnUserService.addressStreet(this.suburbCode);
          } catch (error) {
            console.error(error)
          } finally {
            this.is_searchable = true;
            this.searchCannel = 'local';
            this.allow_free_text = true;
            this.showSelectPopup('Street name');
          }
          break;
        case 'estate':
          try {
            this.selectData = await this.busnUserService.addressEstates('estate');
          } catch (error) {
            console.error(error)
          } finally {
            this.is_searchable = true;
            this.searchCannel = 'api';
            this.allow_free_text = true;
            this.showSelectPopup('Estate');
          }
          break;
        case 'sectional_title':
          try {
            this.selectData = await this.busnUserService.addressSectionalTitles('villas');
          } catch (error) {
            console.error(error)
          } finally {
            this.is_searchable = true;
            this.searchCannel = 'api';
            this.allow_free_text = true;
            this.showSelectPopup('Sectional title');
          }
          break;
      }
    } catch (error) {
      console.warn(error)
    } finally {
      this.sharedConfig.setShowCustomLoader(false);
    }
  }

  showSelectPopup(heading: string) {
    this.selectPopHeading = heading
    this.selectPopup = true;
    this.hideDefualtResults = false;
    if (this.is_searchable) {
      setTimeout(() => this.searchInputRef?.nativeElement?.focus());
    }
  }

  populateAddressPayload(code: string, description: string) {
    switch (this.fieldName) {
      case 'province':
        this.provinceCode = code;
        break;
      case 'suburb':
        this.suburbCode = code;
        break;
      default:
        break;
    }
    this.clearDownstreamFields(this.fieldName);
    this.newAddressPayload[this.fieldName] = this.fieldName === "address_type" ? code : description;
    this.closePopupAndClear();
  }

  clearDownstreamFields(field: string) {
    const downstreamFields: { [key: string]: Array<string> } = {
      address_type: ['province', 'suburb', 'postal_code', 'unit_number', 'estate', 'sectional_title', 'street_number', 'street_name'],
      province: ['suburb', 'postal_code', 'unit_number', 'estate', 'sectional_title', 'street_number', 'street_name'],
      suburb: ['postal_code', 'unit_number', 'estate', 'sectional_title', 'street_number', 'street_name'],
      postal_code: ['unit_number', 'estate', 'sectional_title', 'street_number', 'street_name'],
    };
    downstreamFields[field]?.forEach(downstreamField => this.newAddressPayload[downstreamField] = null);
  }

  async searchData(event: any) {
    let searchText = event.target.value;
    switch (this.searchCannel) {
      case 'local':
        this.searchFound = this.selectData?.filter((str: any) => str.description.toLowerCase().includes(searchText.toLowerCase()));
        this.hideDefualtResults = true;
        break;
      case 'api':
        if (searchText.length > 2) {
          try {
            switch (this.fieldName) {
              case 'estate':
                this.searchFound = await this.busnUserService.addressEstates(searchText);
                this.hideDefualtResults = true;
                this.free_text_message = true;
                break;
              case 'sectional_title':
                this.searchFound = await this.busnUserService.addressSectionalTitles(searchText);
                this.hideDefualtResults = true;
                this.free_text_message = true;
                break;
            }
          } catch (error) {
            console.error(error)
          }
        }
        break;
    }
    if (searchText.length === 0 ) {
      this.hideDefualtResults = false
    }
    if (this.searchFound.length === 0 && this.allow_free_text) {
        this.free_text_message = true;
      } else {
        this.free_text_message = false;
      }
    if (this.allow_free_text) {
      this.manual_text = event.target.value.toUpperCase();
    }
  }

  saveManuallyEnteredText() {
    this.newAddressPayload[this.fieldName] = this.manual_text.toLowerCase();
    this.closePopupAndClear();
  }

  closePopupAndClear() {
    this.allow_free_text = false;
    this.searchFound = new Array;
    this.selectPopup = false;
    this.hideDefualtResults = false;
    this.manual_text = '';
  }

  onPostalCodeChange(value: string) {
    this.newAddressPayload.postal_code = value.replace(/\D/g, '').slice(0, 4);
  }

  toUpperCase(text: string | undefined | null): string | undefined | null {
    return typeof text === 'string' ? text.toUpperCase() : text;
  }

  toLowerCase(text: string | undefined | null): string | undefined | null {
    return typeof text === 'string' ? text.toLowerCase() : text;
  }

  async save() {
    this.sharedConfig.setShowCustomLoader(true)
    for (const key of Object.keys(this.newAddressPayload)) {
      this.newAddressPayload[key] = this.toLowerCase(this.newAddressPayload[key]);
    }
    try {
      let results = await this.busnQuestionService.setAddress(this.newAddressPayload);
      if (results) {
        this.addresses = (await this.busnUserService.getUserAddresses()).sort((a, b) => b.id - a.id);
        this.cancelAddress();
        this.onAddressSelect(results);
      }
    } catch (error) {
      this.sharedDialogService.showPopupDialog({
        header: 'Something went wrong',
        subHeader: 'Your address did not save.',
        buttonOneText: 'OK',
        buttonOneCallback: this.sharedDialogService.hidePopupDialog
      })
    } finally {
      this.sharedConfig.setShowCustomLoader(false);
    }
    console.log(this.newAddressPayload)
  }

  cancelAddress() {
    this.newAddressPopup = false;
    this.cleanPayload();
  }

  isSaveDisabled(): boolean {
    const requiredFields = ['address_type', 'province', 'suburb', 'postal_code'];
    switch (this.newAddressPayload?.address_type) {
      case 'street':
        requiredFields.push('street_number', 'street_name');
        break;
      case 'estate':
        requiredFields.push('unit_number', 'estate', 'street_number', 'street_name');
        break;
      case 'sectional':
        requiredFields.push('unit_number', 'sectional_title', 'street_number', 'street_name');
        break;
    }
    return requiredFields.some(field => !this.newAddressPayload?.[field]) || this.newAddressPayload?.postal_code?.length !== 4;
  }

}
